<script setup lang="ts">
    import { computed, onMounted, ref, watch, type Ref } from 'vue'
    import { useRoute } from 'vue-router'
    import CorporateNavigation from '@common/Components/CorporateNavigation.vue'
    import { useMetricsPageQueryV1 } from '@Quest/Metrics/queries-vue-composables'
    import { useDeleteMetricCommandV1 } from '@Quest/Metrics/commands-vue-composables'
    import type { MetricReadModel, MetricsPageReadModel } from '@Quest/Metrics/readmodels'
    import { ValidationError } from '@common/types'

    const route = useRoute()
    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const strategyId = computed(() => String(route.params.strategyId ?? ''))

    const {
        runMetricsPageQueryV1,
        modelMetricsPageQueryV1
    } = useMetricsPageQueryV1(workspaceId, strategyId)

    const rm = modelMetricsPageQueryV1 as Ref<MetricsPageReadModel | null>

    const {
        invokeDeleteMetricCommandV1,
        onSucceedDeleteMetricCommandV1,
        validationErrorsDeleteMetricCommandV1
    } = useDeleteMetricCommandV1(workspaceId, strategyId)

    const searchPhrase = ref<string | null>(null)
    const filterByMetricType = ref<string | null>(null)

    const metricsView = computed<MetricReadModel[]>(() =>
    {
        if (!rm.value || !rm.value.Metrics) return []

        const phrase = searchPhrase.value?.toLowerCase()
        const typeFilter = filterByMetricType.value

        return rm.value.Metrics.filter(a =>
            (!phrase || a.Name.toLowerCase().includes(phrase)) &&
            (!typeFilter || a.MetricTypeLabel === typeFilter)
        )
    })

    function setFilterByMetricType(metricType: string | null)
    {
        filterByMetricType.value = metricType
    }

    function getEditUrl(m: MetricReadModel): string
    {
        switch (m.MetricType)
        {
            case 'CostSubtotalMetric':
                return `/#/enterprise/edit-cost-subtotal-metric/${workspaceId.value}/${strategyId.value}/${m.Id}`
            case 'ResourceMetric':
                return `/#/enterprise/edit-resource-metric/${workspaceId.value}/${strategyId.value}/${m.Id}`
            case 'ResourceCostMetric':
                return `/#/enterprise/edit-resource-cost-metric/${workspaceId.value}/${strategyId.value}/${m.Id}`
            case 'RevenueMetric':
                return `/#/enterprise/edit-revenue-metric/${workspaceId.value}/${strategyId.value}/${m.Id}`
            case 'MarginCogsMetric':
                return `/#/enterprise/edit-margin-cogs-metric/${workspaceId.value}/${strategyId.value}/${m.Id}`
            case 'TotalCostMetric':
                return `/#/enterprise/edit-total-cost-metric/${workspaceId.value}/${strategyId.value}/${m.Id}`
            case 'CapexMetric':
                return `/#/enterprise/edit-capex-metric/${workspaceId.value}/${strategyId.value}/${m.Id}`
            case 'ExpenseMetric':
                return `/#/enterprise/edit-expense-metric/${workspaceId.value}/${strategyId.value}/${m.Id}`
            case 'OpexMetric':
                return `/#/enterprise/edit-opex-metric/${workspaceId.value}/${strategyId.value}/${m.Id}`
            default:
                return ''
        }
    }

    function getCloneUrl(m: MetricReadModel): string
    {
        switch (m.MetricType)
        {
            case 'CostSubtotalMetric':
                return `/#/enterprise/clone-cost-subtotal-metric/${workspaceId.value}/${strategyId.value}/${m.Id}`
            case 'ResourceMetric':
                return `/#/enterprise/clone-resource-metric/${workspaceId.value}/${strategyId.value}/${m.Id}`
            case 'ResourceCostMetric':
                return `/#/enterprise/clone-resource-cost-metric/${workspaceId.value}/${strategyId.value}/${m.Id}`
            case 'RevenueMetric':
                return `/#/enterprise/clone-revenue-metric/${workspaceId.value}/${strategyId.value}/${m.Id}`
            case 'MarginCogsMetric':
                return `/#/enterprise/clone-margin-cogs-metric/${workspaceId.value}/${strategyId.value}/${m.Id}`
            case 'TotalCostMetric':
                return `/#/enterprise/clone-total-cost-metric/${workspaceId.value}/${strategyId.value}/${m.Id}`
            case 'CapexMetric':
                return `/#/enterprise/clone-capex-metric/${workspaceId.value}/${strategyId.value}/${m.Id}`
            case 'ExpenseMetric':
                return `/#/enterprise/clone-expense-metric/${workspaceId.value}/${strategyId.value}/${m.Id}`
            case 'OpexMetric':
                return `/#/enterprise/clone-opex-metric/${workspaceId.value}/${strategyId.value}/${m.Id}`
            default:
                return ''
        }
    }

    function handleDeleteMetricCommand(id: string)
    {
        if (confirm("Are you sure?") === true)
        {
            invokeDeleteMetricCommandV1({ Id: id })
        }
    }

    onMounted(() =>
    {
        document.title = "Metrics | TransparentChoice"
        runMetricsPageQueryV1()
    })

    onSucceedDeleteMetricCommandV1(() =>
    {
        runMetricsPageQueryV1()
    })

    watch(validationErrorsDeleteMetricCommandV1, (errors: ValidationError[] | null) =>
    {
        if (!errors || errors.length === 0) return

        const error = errors[0]
        alert(error.Error)
    })
</script>

<template>
    <div>
        <CorporateNavigation ActivePage="metrics" />

        <div v-if="rm" class="container" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Metrics</h1>
                </div>
                <div class="col-lg-6 text-right align-self-end">
                    <div class="dropdown d-inline">
                        <button class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown" style="padding: 0.7em 2em;" data-testid="metrics-page-create-button">
                            Create metric
                        </button>
                        <div class="dropdown-menu dropdown-menu-right dropdown-menu-scrollable" role="menu">
                            <a class="dropdown-item" v-sanitizedHref="`/#/enterprise/create-revenue-metric/${workspaceId}/${strategyId}`" data-testid="metrics-page-create-revenue">Revenue</a>
                            <a class="dropdown-item" v-sanitizedHref="`/#/enterprise/create-margin-cogs-metric/${workspaceId}/${strategyId}`" data-testid="metrics-page-create-margin-cogs">COGS (Margin)</a>
                            <div class="dropdown-divider"></div>
                            <a class="dropdown-item" v-sanitizedHref="`/#/enterprise/create-resource-metric/${workspaceId}/${strategyId}`" data-testid="metrics-page-create-resource">Resource (units)</a>
                            <a class="dropdown-item" v-sanitizedHref="`/#/enterprise/create-resource-cost-metric/${workspaceId}/${strategyId}`" data-testid="metrics-page-create-resource-cost">Resource (money)</a>
                            <div class="dropdown-divider"></div>
                            <a class="dropdown-item" v-sanitizedHref="`/#/enterprise/create-total-cost-metric/${workspaceId}/${strategyId}`" data-testid="metrics-page-create-total-cost">Total cost</a>
                            <a class="dropdown-item" v-sanitizedHref="`/#/enterprise/create-expense-metric/${workspaceId}/${strategyId}`" data-testid="metrics-page-create-expense">Expense</a>
                            <a class="dropdown-item" v-sanitizedHref="`/#/enterprise/create-cost-subtotal-metric/${workspaceId}/${strategyId}`" data-testid="metrics-page-create-cost-subtotal">Cost subtotal</a>
                            <a class="dropdown-item" v-sanitizedHref="`/#/enterprise/create-opex-metric/${workspaceId}/${strategyId}`" data-testid="metrics-page-create-opex">Opex</a>
                            <a class="dropdown-item" v-sanitizedHref="`/#/enterprise/create-capex-metric/${workspaceId}/${strategyId}`" data-testid="metrics-page-create-capex">Capex</a>
                        </div>
                    </div>
                </div>
            </div>

            <hr style="border-color: #bbb; margin: 20px 0 60px;" />

            <div v-if="rm" class="d-flex flex-wrap mb-3">
                <!-- Search Field  -->
                <div style="width: 250px;">
                    <input type="search"
                           class="form-control"
                           placeholder="Search by name..."
                           v-model="searchPhrase" data-testid="metrics-page-search">
                </div>

                <!-- Type Filter Dropdown (Left Side) -->
                <div class="dropdown ml-3">
                    <button class="btn"
                            :class="filterByMetricType ? 'btn-info' : 'btn-outline-secondary'"
                            type="button"
                            data-toggle="dropdown" data-testid="metrics-page-filter-button">
                        <i class="fas fa-filter mr-1"
                           :class="filterByMetricType ? 'text-warning' : 'text-muted'">
                        </i>
                        <span>Metric type</span>
                    </button>
                    <div class="dropdown-menu dropdown-menu-right dropdown-menu-scrollable">
                        <a class="dropdown-item"
                           :class="{ 'active': filterByMetricType === null }"
                           @click="setFilterByMetricType(null)" data-testid="metrics-page-filter-any">
                            Any / No filter
                        </a>
                        <div class="dropdown-divider"></div>
                        <h6 class="dropdown-header">Filter by metric type</h6>
                        <a class="dropdown-item"
                           v-for="(m, index) in rm.MetricTypes"
                           :key="m"
                           :class="{ 'active': filterByMetricType === m }"
                           @click="setFilterByMetricType(m)" :data-testid="`metrics-page-filter-${index}`">
                            {{ m }}
                        </a>
                    </div>
                </div>
            </div>
            <div class="mt-3">
                <table class="table table-bordered table-hover" data-testid="metrics-page-table">
                    <thead>
                        <tr>
                            <th>Metric</th>
                            <th>Type</th>
                            <th style="width: 120px;"></th>
                        </tr>

                    </thead>
                    <tbody>
                        <tr v-for="(m, index) in metricsView" :key="m.Id" :data-testid="`metrics-page-row-${index}`">
                            <td>
                                <a class="text-primary" v-sanitizedHref="getEditUrl(m)" :data-testid="`metrics-page-edit-link-${index}`">{{m.Name}}</a>
                                <p class="text-muted">{{m.Description}}</p>
                            </td>
                            <td>
                                {{m.MetricTypeLabel}}
                            </td>
                            <td>
                                <div class="dropdown">
                                    <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown" :data-testid="`metrics-page-actions-button-${index}`">
                                        Actions
                                    </button>
                                    <div class="dropdown-menu dropdown-menu-right">
                                        <a class="dropdown-item" v-sanitizedHref="getEditUrl(m)" :data-testid="`metrics-page-actions-edit-${index}`">Edit</a>
                                        <a class="dropdown-item" v-sanitizedHref="getCloneUrl(m)" :data-testid="`metrics-page-actions-clone-${index}`">Clone</a>
                                        <div class="dropdown-divider"></div>
                                        <a class="dropdown-item" @click="handleDeleteMetricCommand(m.Id)" :data-testid="`metrics-page-actions-delete-${index}`">Delete</a>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>